<template>
  <section
    v-if="eventStatus === 'loading'
      || ($store.state.events.eventTransactionsStatus === 'loading'
      && $store.state.events.updateTransactionsStatus !== 'success')"
    class="transaction-panel no-transaction">
    <app-loader
      class="loader"
    />
  </section>
  <section
    v-else-if="(transactions.length)
    || ($store.state.events.updateTransactionsStatus === 'success')"
    class="transaction-panel"
    :class="{'newTransitionsNotificationHidden': isNotificationNewTransactionsHidden}">
    <transaction
      :enable-transition="isComponentLoaded"
      @ready="onTransactionReady"
      v-for="(transaction, index) in transactions"
      :key="transaction.id"
      :transaction="transaction"
      :event-transactions="transactions"
      :user="user"
      :last-seen-transaction-id="event.last_read_operation_id"
      :is-last-transaction="transaction.id === transactions[transactions.length - 1].id"
      :event-contacts="event.contacts"
      :show-date-time="isDateTimeShown(transaction, index)"
      :is-mobile="isMobile"
      :scroll-observer="transactionsScrollObserver"
      position="right"/>
    <div class="event-info">
      <div
        class="user-balance"
      >
        {{event.balance ? getPayoutMessage(event.balance) : ''}}
        {{transactions && transactions.length && !event.balance ? $t('CP.noDebts') : ''}}
        <money-message
          v-if="typeof event.balance === 'number' && event.balance"
          :money-amount="Math.abs(event.balance)"
          :currency="eventCurrency"
          :type="event.balance | getPayoutType"
          font-size="16px"
        />
      </div>
      <div class="user-turn">
        {{ getPayoutStatus }}
      </div>
    </div>
    <div ref="scrollDownTrigger" class="transaction-panel__scrollDownTrigger"></div>
    <div class="transaction-panel__scrollDownWrapper">
      <button @click="scrollToBottom"
              class="transaction-panel__scrollDown"
              :class="{'is-visible': isScrollToBottomVisible}">
        <font-awesome-icon icon="chevron-down" />
      </button>
    </div>
  </section>
  <section
    v-else-if="!transactions.length"
    class="transaction-panel no-transaction">
    <div v-if="!transactions.length" class="no-transaction">
      <img src="../../assets/img/sad.svg" alt="" class="no-transaction__img">
      <p class="no-transaction__text">
        {{ $t('CP.noTransactionsYet') }}
      </p>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { MARK_OPERATION_SEEN } from '@/store/operations/types';
import {
  CLEAR_EVENT_EXPENSES_UPDATE_STATUS,
  CLEAR_OPERATIONS_UPDATE_STATUS,
} from '@/store/events/types';
import Transaction from '../Transaction/Transaction';
import MoneyMessage from '../MoneyMessagge/MoneyMessage';
import AppLoader from '../AppLoader/AppLoader';

export default {
  name: 'TransactionPanel',
  components: {
    AppLoader,
    MoneyMessage,
    Transaction,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    transactions: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
    },
    event: {
      type: Object,
      default: () => ({}),
    },
    eventCurrency: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isComponentLoaded: false,
      transactionsScrollObserver: null,
      isScrollToBottomVisible: false,
      isNotificationNewTransactionsHidden: false,
      scrollDownTrigger: null,
    };
  },
  mounted() {
    this.isComponentLoaded = true;
    this.transactionsScrollObserver = new IntersectionObserver(this.scrollObserverHandler, {
      root: this.$el,
      rootMargin: '0px',
      threshold: [0, 0.5, 1],
    });
  },
  watch: {
    transactions: {
      immediate: true,
      handler() {
        this.bindScrollTrigger();
      },
    },
    eventStatus: {
      immediate: true,
      handler() {
        this.bindScrollTrigger();
      },
    },
  },
  beforeDestroy() {
    this.isComponentLoaded = false;
    this.$store.commit(CLEAR_OPERATIONS_UPDATE_STATUS);
    this.$store.commit(CLEAR_EVENT_EXPENSES_UPDATE_STATUS);
  },
  computed: {
    ...mapState({
      eventStatus: (state) => state.events.eventStatus,
      updateTransactionsStatus: (state) => state.events.updateTransactionsStatus,
    }),
    getPayoutStatus() {
      if (this.event.contacts && this.event.turn) {
        if (this.event.turn.uid === this.user.user) {
          return this.$t('CP.myTurnToPay');
        }
        const contactToPay = this.event.contacts
          .find((contact) => contact.uid === this.event.turn.uid);
        if (contactToPay) {
          return `${this.$t('CP.turnToPay')}: ${this.$getUserName(contactToPay)}`;
        }
      }
      return '';
    },
  },
  methods: {
    bindScrollTrigger() {
      this.$nextTick(() => {
        if (this.$refs.scrollDownTrigger) {
          this.transactionsScrollObserver.observe(this.$refs.scrollDownTrigger);
        }
      });
    },
    scrollObserverHandler(entries, observer) {
      entries.forEach((entry) => {
        if (entry.target.classList.contains('transaction-panel__scrollDownTrigger')) {
          // toggle scrollToBottom button
          if (entry.rootBounds.bottom < entry.boundingClientRect.top) {
            this.isScrollToBottomVisible = true;
          } else {
            this.isScrollToBottomVisible = false;
          }
        } else if (entry.target.classList.contains('transaction_titleNew')
          && entry.intersectionRatio <= 0) {
          // hide notofication about new messages
          if (entry.rootBounds.top > entry.boundingClientRect.bottom) {
            observer.unobserve(entry.target);
            this.isNotificationNewTransactionsHidden = true;
          }
        }

        if (entry.target.classList.contains('transaction-container')
          && entry.isIntersecting
          && entry.intersectionRatio > 0.4) {
          // mark operations as seen
          observer.unobserve(entry.target);
          this.$store.dispatch(MARK_OPERATION_SEEN, {
            operationId: entry.target.dataset.transactionId,
            markAll: false,
          });
        }
      });
    },
    scrollToBottom() {
      this.transactionsScrollObserver.disconnect();
      this.transactionsScrollObserver.observe(this.$refs.scrollDownTrigger);
      this.$el.scrollTo({
        top: this.$el.scrollHeight,
        behavior: 'smooth',
      });
      this.isNotificationNewTransactionsHidden = true;
      this.$store.dispatch(MARK_OPERATION_SEEN, {
        operationId: this.transactions[this.transactions.length - 1].id,
        markAll: true,
      });
    },
    isDateTimeShown(transaction, index) {
      if (this.transactions.length - 1 === index) return true;
      const transactionDate = this.$moment(`${transaction && transaction.cr_date}Z`);
      const nextTransactionDate = this.$moment(`${(this.transactions && this.transactions[index + 1]
        && this.transactions[index + 1].cr_date)}Z`);
      return ((transactionDate.date() !== nextTransactionDate.date())
        || (transactionDate.month() !== nextTransactionDate.month()));
    },
    onTransactionReady(transactionEl) {
      // eslint-disable-next-line no-unused-expressions
      transactionEl?.$el?.scrollIntoView?.({ block: 'center' });
    },
    getPayoutMessage(balance) {
      let message = '';
      if (balance > 0) {
        message = `${this.$t('CP.theyOweMe')} `;
      }
      if (balance < 0) {
        message = `${this.$t(`CP.iOweThem_${this.user.gender || '_M'}`)} `;
      }
      return message;
    },
  },
};
</script>

<style lang="scss" scoped>
.transaction-panel {
  padding: 0 30px 20px;
  height: calc(100% - 63px - 130px - 0px);
  min-height: 162px;
  overflow-y: auto;
  max-width: 100vw;
}

.event-info {
  margin-top: 41px;
  text-align: right;
}

.user-balance {
  font-size: 16px;
}

.user-turn {
  color: var(--text-seccond-color);
  font-size: 12px;
}

.no-transaction {
  display: flex;
  justify-content: center;
  align-items: center;

  .transaction-container {
    display: none;
  }
}

.loader {
  margin: auto;
}

.no-transaction__img {
  width: 73px;
  height: 52px;
}

.no-transaction__text {
  color: #303F9F;
  opacity: 0.2;
  margin-bottom: 0;
}

.transaction-panel__scrollDownTrigger {
  height: 1px;
  transform: translateY(-500px);
}
.transaction-panel__scrollDownWrapper {
  align-items: flex-end;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  height: 0;
  position: sticky;
}
.transaction-panel__scrollDown {
  align-items: center;
  appearance: none;
  background-color: var(--btn-primary);
  border: none;
  border-radius: 50%;
  color: #fff;
  display: flex;
  height: 45px;
  justify-content: center;
  opacity: 0;
  padding: 0;
  transform: translateY(100%);
  transition: .135s ease;
  visibility: hidden;
  width: 45px;

  &:hover,
  &:focus,
  &:active {
    background-color: #256DE6;
    outline: none;
  }
  &.is-visible {
    opacity: 1;
    transform: none;
    visibility: visible;
  }
}

/* @media screen and (max-width: $breakpoint-sm) {
  .transaction-panel {
    padding-bottom: 25vh;
  }
} */
</style>
